import type { BuildPanelBucket } from './buildApi';
import { panelSplitLabel, previewPanelSplit, validSerkenPercent, type PanelSplit } from './panel-split';

export function PanelSplitFields({ id, value, onChange }: {
  id: string;
  value: PanelSplit;
  onChange: (change: Partial<PanelSplit>) => void;
}): JSX.Element {
  const percent = value.serken_percent ?? 50;
  const valid = validSerkenPercent(percent);
  return (
    <div className="bl-field">
      <label className="bl-field-label" htmlFor={`${id}-bucket`}>Panel bucket</label>
      <select id={`${id}-bucket`} className="bl-input" value={value.panel_bucket}
        onChange={(e) => onChange({ panel_bucket: e.target.value as BuildPanelBucket })}>
        <option value="serken">Serken only · 100%</option>
        <option value="regular">Regular only · 100%</option>
        <option value="mixed">Mixed · custom percentage</option>
      </select>
      {value.panel_bucket === 'mixed' && (
        <>
          <div className="bl-formgrid">
            <div className="bl-field">
              <label className="bl-field-label" htmlFor={`${id}-serken`}>Serken (%)</label>
              <input id={`${id}-serken`} className="bl-input" type="number" min={0} max={100} step={1}
                value={Number.isFinite(percent) ? percent : ''} aria-invalid={!valid}
                onChange={(e) => onChange({ serken_percent: e.target.value === '' ? NaN : Number(e.target.value) })} />
            </div>
            <div className="bl-field">
              <label className="bl-field-label" htmlFor={`${id}-regular`}>Regular (%)</label>
              <input id={`${id}-regular`} className="bl-input" type="number" min={0} max={100} step={1}
                value={Number.isFinite(percent) ? 100 - percent : ''} aria-invalid={!valid}
                onChange={(e) => onChange({ serken_percent: e.target.value === '' ? NaN : 100 - Number(e.target.value) })} />
            </div>
          </div>
          <div className="bl-actions">
            {[50, 60, 70].map((p) => (
              <button key={p} type="button" className="bl-btn bl-btn--secondary bl-btn--small"
                aria-label={`${p}% Serken, ${100 - p}% regular`} aria-pressed={percent === p}
                onClick={() => onChange({ serken_percent: p })}>{p}/{100 - p}</button>
            ))}
          </div>
          {!valid && <span className="bl-notice bl-notice--err" role="alert">Enter a whole percentage from 0 to 100.</span>}
        </>
      )}
      <span className="bl-hint">
        Percentages apply to whole domains, rounded to the nearest domain. Each domain’s inboxes stay together.
        With equal inboxes per domain, the inbox split follows the same ratio.
      </span>
    </div>
  );
}

export function PanelSplitSummary({ rows, split }: {
  rows: readonly { email: string }[];
  split: PanelSplit;
}): JSX.Element | null {
  const totals = previewPanelSplit(rows, split);
  if (!totals || rows.length === 0) return null;
  return (
    <div className="bl-field" aria-live="polite">
      <span className="bl-field-label">Order split · {panelSplitLabel(split)}</span>
      <table className="bl-table">
        <thead><tr><th>Panel type</th><th>Domains</th><th>Inboxes</th></tr></thead>
        <tbody>
          {(['serken', 'regular'] as const).map((kind) => (
            <tr key={kind}><td>{kind === 'serken' ? 'Serken' : 'Regular'}</td>
              <td>{totals[kind].domains}</td><td>{totals[kind].inboxes}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
